import {
  DashboardComponentNames,
  filterNotEmptyValues,
  FiltersToolbar,
  IDashboardComponentProperties,
  IFlow,
  ILayout,
  IQuickFilterProperty,
  makeDynamicReportingPageStore,
  PageViewModeEnum,
  PageViewModeSelector,
  useFilters,
  usePerformanceReportBuilderPageStore,
} from '@diana/core';
import { Box } from '@mui/material';
import { isEqual, uniq } from 'lodash';
import { MutableRefObject, useLayoutEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { createPropertyToFilter } from '../PerformanceReportPage/Support';
import { useUpdateLayoutQuickFilterPropertyMutation } from './Mutations/useUpdateLayoutQuickFilterPropertyMutation';

export const PerformanceReportBuilderPageToolbar = (props: {
  reportId: string;
  flows: IFlow[];
  layout: ILayout<IDashboardComponentProperties, DashboardComponentNames>;
  gridApiRef?: MutableRefObject<any>;
  height?: number;
}) => {
  const { reportId, flows, layout, gridApiRef, height } = props;

  const quickFilterPropertyInitializedRef = useRef(false);

  const defaultQuickFilterProperty = usePerformanceReportBuilderPageStore(
    (s) => s.defaultQuickFilterProperty,
  );

  const useReportingPageStore = makeDynamicReportingPageStore(reportId);
  const filters = useReportingPageStore((s) => s.filters);
  const setFilters = useReportingPageStore((s) => s.setFilters);
  const filtersValues = useReportingPageStore((s) => s.filtersValues);
  const setFiltersValues = useReportingPageStore((s) => s.setFiltersValues);
  const availableFilters = useReportingPageStore((s) => s.availableFilters);
  const setAvailableFilters = useReportingPageStore((s) => s.setAvailableFilters);
  const viewMode = useReportingPageStore((s) => s.viewMode);
  const setViewMode = useReportingPageStore((s) => s.setViewMode);

  const { mutate: updateQuickFilterProperty } = useUpdateLayoutQuickFilterPropertyMutation();
  const updateQuickFilterPropertyDebounce = useDebouncedCallback(updateQuickFilterProperty, 1500);

  const { defaultData, onFilterChange, normalizeFiltersValues } = useFilters({
    flows,
    createPropertyToFilter,
    layoutData: { ...layout, quickFilterProperty: defaultQuickFilterProperty },
    filtersValues,
    setFiltersValues,
    availableFilters,
    withDefaultFiltersValuesFromFlow: false,
    withoutQuickFilters: true,
  });

  useLayoutEffect(() => {
    quickFilterPropertyInitializedRef.current = false;
  }, [reportId]);

  useLayoutEffect(() => {
    if (layout && availableFilters.length && !quickFilterPropertyInitializedRef.current) {
      quickFilterPropertyInitializedRef.current = true;

      const result = layout.quickFilterProperty?.keys ?? {};
      const filtersValues = normalizeFiltersValues({ filtersValues: result, availableFilters });
      setFiltersValues(filterNotEmptyValues(filtersValues));
      setFilters(uniq([...Object.keys(filtersValues)]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters, layout?.quickFilterProperty?.keys, setFiltersValues, setFilters]);

  useLayoutEffect(() => {
    if (quickFilterPropertyInitializedRef.current) {
      const newQuickFilterProperty = filters.reduce<IQuickFilterProperty>(
        (quickFilterProperty, filter) => {
          if (filtersValues[filter]) {
            quickFilterProperty.keys[filter] = filtersValues[filter].map(
              (v) => v.value,
            ) as string[];
          }
          return quickFilterProperty;
        },
        { keys: {} },
      );

      if (layout && !isEqual(newQuickFilterProperty, layout.quickFilterProperty)) {
        updateQuickFilterPropertyDebounce(newQuickFilterProperty);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, filtersValues]);

  return (
    <Box sx={{ width: '100%', '& > .MuiStack-root > .MuiStack-root': { pb: 0 } }}>
      <FiltersToolbar
        reportId={reportId}
        filters={filters}
        setFilters={setFilters}
        filtersValues={filtersValues}
        setFiltersValues={setFiltersValues}
        availableFilters={availableFilters}
        setAvailableFilters={setAvailableFilters}
        onFilterChange={onFilterChange}
        defaultData={defaultData}
        gridApiRef={gridApiRef}
        height={height}
        endContent={
          <Box pl={2}>
            <PageViewModeSelector
              viewMode={viewMode}
              supportedViewModes={[PageViewModeEnum.Board, PageViewModeEnum.Table]}
              onViewChange={setViewMode}
            />
          </Box>
        }
      />
    </Box>
  );
};
